import { API_BASE_URL } from './fetchAPI';

/** How many photos a post or an event carries — `Media::MAX_PER_CONTENT`. */
export const MAX_PHOTOS = 4;

/**
 * Where a photo is read. Two sizes exist: `thumb` (480 px) for a list, `large`
 * (1600 px) for a page of its own. The API decides who may see it, so the
 * same URL answers a visitor, a neighbour or the author differently.
 */
export function mediaUrl(uuid, variant = 'large') {
  return `${API_BASE_URL}/medias/${uuid}/${variant}`;
}

/** Past this, a photo is shrunk in the browser before it is sent. */
const MAX_EDGE = 2000;

/**
 * Shrink a phone's photo before sending it: 8 MB over a mobile connection is
 * a long wait for something the server will bring down to a few hundred
 * kilobytes anyway. The server stays the authority — it re-encodes whatever
 * arrives — so anything the browser cannot decode (HEIC outside Safari, an
 * old browser) goes as it is.
 */
export async function shrinkForUpload(file) {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    return file;
  }

  try {
    // 'from-image' applies the EXIF orientation, so the pixels are already
    // upright when the canvas drops the EXIF block.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));

    if (scale === 1 && file.size < 2 * 1024 * 1024) {
      bitmap.close?.();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));

    return blob ? new File([blob], 'photo.jpg', { type: 'image/jpeg' }) : file;
  } catch {
    return file;
  }
}
